import { useState, type FC } from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Textarea,
  Text,
} from '@chakra-ui/react';
import { ThemeButton } from './ThemeButton';
import { ReactComponent as GreenIncorrectInformationIcon } from '../../../../assets/svg/GreenIncorrectInformationIcon.svg';
import { translate } from '../../../../constants/translation';
import { InformationPanelAction, Event, type Page, type VesselViewModel } from '../../../../types';
import ConfirmFeedbackCard, { ConfirmCardSource } from '../../../ConfirmFeedbackCard';
import { getEventFactory } from '../../../../utils/get-event-factory';
import { useAppInsights } from '../../../../hooks/useAppInsights';
import { type Timestamp } from '../../../../services/Timestamp';

interface SeeSomethingWrongModalProps {
  page: Page;
  vessel: VesselViewModel;
  timestamp: Timestamp;
  timezone: string;
  isOpen: boolean;
  onModalClose: () => void;
}

const SeeSomethingWrong: FC<SeeSomethingWrongModalProps> = ({
  page,
  vessel,
  timestamp,
  timezone,
  isOpen,
  onModalClose,
}) => {
  const appInsights = useAppInsights();
  const listOfThemes = [
    translate('THEME_1'),
    translate('THEME_2'),
    translate('THEME_3'),
    translate('THEME_4'),
    translate('THEME_5'),
    translate('THEME_6'),
    translate('THEME_7'),
  ];

  const [selectedButtons, setSelectedButtons] = useState<string[]>([]);
  const [textArea, setTextArea] = useState('');
  const [openConfirmModal, setOpenConfirmModal] = useState(false);

  const submitIncorrectInformation = (operation: InformationPanelAction): void => {
    const dateString = timezone && timestamp ? timestamp.toLocationDateTimeOffset(timezone).dateString : '';

    const event = getEventFactory(Event.WrongInformation)(
      page,
      vessel,
      dateString,
      selectedButtons,
      textArea,
      encodeURIComponent(window.location.href),
      operation
    );
    appInsights.trackAnalyticsEvent(event);
  };

  const handleButtonClick = (value: string): void => {
    if (!selectedButtons.includes(value)) {
      setSelectedButtons(prev => [...prev, value]);
    } else {
      setSelectedButtons(prev => prev.filter(item => item !== value));
    }
  };

  const handleClose = (operation: InformationPanelAction): void => {
    submitIncorrectInformation(operation);
    setSelectedButtons([]);
    onModalClose();
    setTextArea('');
    if (operation === InformationPanelAction.Submit) {
      setOpenConfirmModal(true);
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={() => {
          handleClose(InformationPanelAction.Cancel);
        }}
      >
        <ModalOverlay />
        <ModalContent>
          <GreenIncorrectInformationIcon className="modal-icon" />
          <ModalHeader>{translate('MODAL_HEADING')}</ModalHeader>
          <ModalCloseButton className="modal-close-btn" />
          <ModalBody className="incorrect-information-modal">
            <Text>{translate('MODAL_SUB_HEADING')}</Text>
            <div className="theme-button-area">
              {listOfThemes.map((x, i) => (
                <ThemeButton
                  key={`theme-button-${i}-${x}`}
                  text={x}
                  onButtonClick={handleButtonClick}
                  isModalOpen={isOpen}
                />
              ))}
            </div>
            <Text>{translate('MODAL_TEXTAREA_DESCRIPTION')}</Text>
            <Textarea
              placeholder={translate('MODAL_TEXTAREA')}
              value={textArea}
              onChange={e => {
                setTextArea(e.target.value);
              }}
              aria-label="description"
              size="md"
            />
          </ModalBody>
          <ModalFooter justifyContent="space-between">
            <Button
              variant="secondary"
              onClick={() => {
                handleClose(InformationPanelAction.Cancel);
              }}
              float="left"
              width="48%"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              isDisabled={selectedButtons.length < 1}
              onClick={() => {
                handleClose(InformationPanelAction.Submit);
              }}
              padding-left="5px"
              width="48%"
            >
              Submit
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ConfirmFeedbackCard
        isOpen={openConfirmModal}
        source={ConfirmCardSource.SeeSomethingWrongReport}
        onClose={() => {
          setOpenConfirmModal(false);
        }}
      />
    </>
  );
};

export default SeeSomethingWrong;
